import React, { useState } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';

const FeedbackForm = () => {
  const [studentName, setStudentName] = useState('');
  const [courseName, setCourseName] = useState('');
  const [homeworkText, setHomeworkText] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileName, setFileName] = useState('');
  const [processInfo, setProcessInfo] = useState(null);
  const [similarDocuments, setSimilarDocuments] = useState([]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setLoading(true);
    setError('');
    setSuccess(false);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/api/upload', formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      setHomeworkText(response.data.text);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred while uploading the file');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await axios.post('/api/feedback', {
        student_name: studentName,
        course_name: courseName,
        text: homeworkText,
      });

      setFeedback(response.data.feedback);
      setProcessInfo(response.data.process_info);
      setSimilarDocuments(response.data.similar_documents);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred while generating feedback');
    } finally {
      setLoading(false);
    }
  };

  const renderSimilarDocuments = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Similar Documents Used</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {similarDocuments.map((doc, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1">
                Course: {doc.course_name}
              </Typography>
              <Chip 
                label={`Similarity: ${(doc.similarity * 100).toFixed(1)}%`}
                color="primary"
                size="small"
              />
            </Box>
            <Typography variant="subtitle2" gutterBottom>
              Title: {doc.title}
            </Typography>
            {doc.url && (
              <Typography variant="subtitle2" gutterBottom>
                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                  View Original Document
                </a>
              </Typography>
            )}
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {doc.feedback}
            </Typography>
          </Paper>
        ))}
      </AccordionDetails>
    </Accordion>
  );

  const renderProcessInfo = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Process Information</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography>
            <strong>Student Name:</strong> {processInfo.student_name}
          </Typography>
          <Typography>
            <strong>Content Length:</strong> {processInfo.content_length} characters
          </Typography>
          <Typography>
            <strong>Embedding Length:</strong> {processInfo.embedding_length} dimensions
          </Typography>
          <Typography>
            <strong>Number of Similar Documents:</strong> {processInfo.num_similar_docs}
          </Typography>
          <Typography>
            <strong>Model Used:</strong> {processInfo.model_used}
          </Typography>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">System Prompt</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {processInfo.system_prompt}
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">User Prompt</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {processInfo.user_prompt}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderSummarizationInfo = (summarizationInfo) => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Text Processing Information</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Input Text Summarization */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Input Text Processing
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                Original Tokens: {summarizationInfo.input_text.original_tokens}
              </Typography>
              <Typography>
                Final Tokens: {summarizationInfo.input_text.final_tokens}
              </Typography>
              {summarizationInfo.input_text.was_summarized && (
                <>
                  <Typography>
                    Summarization Rounds: {summarizationInfo.input_text.rounds}
                  </Typography>
                  <Typography variant="subtitle2" gutterBottom>
                    Summarization History:
                  </Typography>
                  {summarizationInfo.input_text.summarization_history.map((round, index) => (
                    <Box key={index} sx={{ pl: 2 }}>
                      <Typography>
                        Round {round.round}: {round.tokens_before} → {round.tokens_after} tokens
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Paper>

          {/* Context Summarization */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Context Processing
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                Original Tokens: {summarizationInfo.context.original_tokens}
              </Typography>
              <Typography>
                Final Tokens: {summarizationInfo.context.final_tokens}
              </Typography>
              {summarizationInfo.context.was_summarized && (
                <>
                  <Typography>
                    Summarization Rounds: {summarizationInfo.context.rounds}
                  </Typography>
                  <Typography variant="subtitle2" gutterBottom>
                    Summarization History:
                  </Typography>
                  {summarizationInfo.context.summarization_history.map((round, index) => (
                    <Box key={index} sx={{ pl: 2 }}>
                      <Typography>
                        Round {round.round}: {round.tokens_before} → {round.tokens_after} tokens
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderCostInfo = () => {
    if (!processInfo?.cost_info) return null;

    const costInfo = processInfo.cost_info;
    const totalCost = (
      (costInfo.embedding_cost || 0) +
      (costInfo.summarization_cost || 0) +
      (costInfo.completion_cost || 0)
    ).toFixed(4);

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Cost Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Total Cost: ${totalCost}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {costInfo.embedding_cost && (
                  <Typography>
                    <strong>Embedding Cost:</strong> ${costInfo.embedding_cost.toFixed(4)}
                  </Typography>
                )}
                {costInfo.summarization_cost && (
                  <Typography>
                    <strong>Summarization Cost:</strong> ${costInfo.summarization_cost.toFixed(4)}
                  </Typography>
                )}
                {costInfo.completion_cost && (
                  <Typography>
                    <strong>Completion Cost:</strong> ${costInfo.completion_cost.toFixed(4)}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h4" component="h1" gutterBottom>
        Homework Feedback
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Student Name"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Course Name"
            value={courseName}
            onChange={(e) => setCourseName(e.target.value)}
            margin="normal"
            required
          />
          
          <Box sx={{ mt: 2, mb: 2 }}>
            <input
              accept=".txt,.doc,.docx,.pdf"
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={loading}
              >
                Upload Homework File
              </Button>
            </label>
            {fileName && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected file: {fileName}
              </Typography>
            )}
            {uploadProgress > 0 && (
              <Box sx={{ width: '100%', mt: 1 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="body2" color="text.secondary" align="right">
                  {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Box>

          <TextField
            fullWidth
            label="Homework Text"
            value={homeworkText}
            onChange={(e) => setHomeworkText(e.target.value)}
            margin="normal"
            required
            multiline
            rows={10}
            disabled={loading}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Generate Feedback'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && feedback && (
        <>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Generated Feedback
            </Typography>
            <Typography
              component="div"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
              }}
            >
              {feedback}
            </Typography>
          </Paper>

          {processInfo?.cost_info && renderCostInfo()}
          {processInfo?.summarization_info && renderSummarizationInfo(processInfo.summarization_info)}
          {processInfo && renderProcessInfo()}
          {similarDocuments.length > 0 && renderSimilarDocuments()}
        </>
      )}
    </Container>
  );
};

export default FeedbackForm; 